/**
 * SkinSprite — the RENDERING half of SkinResolver, split along the
 * layer seam (file-size rule): SkinResolver.js keeps the data/config
 * side (skins, settings, tilesets, persistence); this module turns a
 * resolved slot descriptor into live Pixi objects:
 *
 *   buildSlotSprite       descriptor → (Animated)Sprite with lazy
 *                         texture load into the resolver's cache
 *   preloadSlotTexture    eagerly decode a slot's texture (preloaders)
 *   placeholderGraphic    fallbackColor rect when a slot has no art
 *   clampScale            fit/cover/stretch/native scale modes
 *   resolveGridTexture    atlas-cell sub-texture with cache
 *
 * Every function takes the live SkinResolver instance first and uses
 * its private caches (`_textureCache`, `_subTextureCache`) directly —
 * this module is a private half of the resolver, not a public API.
 */

import { AnimatedSprite, Graphics, Rectangle, Sprite, Texture } from '../../vendor/pixi.min.mjs';

export function sanitizeAtlasGrid(atlas) {
    if (!atlas || typeof atlas !== 'object') return null;
    const gridCols = Math.max(1, Number(atlas.gridCols) || 1);
    const gridRows = Math.max(1, Number(atlas.gridRows) || 1);
    const frameCol = Math.max(0, Math.min(gridCols - 1, Number(atlas.frameCol) || 0));
    const frameRow = Math.max(0, Math.min(gridRows - 1, Number(atlas.frameRow) || 0));
    const spanCols = Math.max(1, Math.min(gridCols - frameCol, Number(atlas.spanCols) || 1));
    const spanRows = Math.max(1, Math.min(gridRows - frameRow, Number(atlas.spanRows) || 1));
    return { gridCols, gridRows, frameCol, frameRow, spanCols, spanRows };
}

export function isFrameAnimation(descriptor) {
    return descriptor?.animation?.mode === 'frames';
}

/**
 * Split a legacy slot key like 'snake.tram.head' into ['snake', 'tram.head'].
 * Returns [null, key] when no game prefix is present.
 */
export function splitSlotKey(slotKey) {
    if (typeof slotKey !== 'string') return [null, null];
    const dot = slotKey.indexOf('.');
    if (dot < 0) return [null, slotKey];
    return [slotKey.slice(0, dot), slotKey.slice(dot + 1)];
}

/** Extracts the game id from a legacy slot key or returns the arg unchanged. */
export function extractGame(arg) {
    if (typeof arg !== 'string') return null;
    const dot = arg.indexOf('.');
    return dot < 0 ? arg : arg.slice(0, dot);
}

/**
 * Body of `SkinResolver.sprite()` — build a Sprite/AnimatedSprite for a
 * slot, loading its texture lazily into the resolver's shared cache.
 * Returns null when the slot resolves to no art.
 */
export function buildSlotSprite(resolver, game, slot, opts = {}) {
    const {
        width = 64,
        height = 64,
        anchorX = 0.5,
        anchorY = 0.5,
        fitMode = 'contain',
        scaleMultiplier = 1,
        flipX = false,
        src: forcedSrc = null,
        region = null,
    } = opts;

    // `opts.src` lets callers (e.g. FroggerBirds) pre-pick which
    // multi-variant default they want so the trail/colour matches.
    // When provided we bypass the resolver's random pick entirely.
    let descriptor = forcedSrc
        ? { src: forcedSrc }
        : resolver._resolveDescriptor(game, slot);
    const src = descriptor?.src || null;
    if (!src) return null;

    // `opts.region` overrides the stored mapping with an explicit
    // atlas grid region — used by autotiling, where ONE imported
    // template image serves 16 sub-tiles picked at paint time.
    // Region sprites are always static; the slot's own animation
    // metadata is ignored for this render.
    if (region) {
        descriptor = {
            ...descriptor,
            mappingType: 'gridRegion',
            animation: null,
            atlas: {
                gridCols: region.gridCols, gridRows: region.gridRows,
                frameCol: region.frameCol, frameRow: region.frameRow,
                spanCols: region.spanCols || 1, spanRows: region.spanRows || 1,
            },
        };
    }

    const animated = isFrameAnimation(descriptor);
    const sprite = animated
        ? new AnimatedSprite({
            textures: [Texture.EMPTY],
            autoUpdate: true,
            autoPlay: false,
            loop: descriptor?.animation?.loop !== false,
            animationSpeed: Math.max(1, descriptor?.animation?.fps || 8) / 60,
        })
        : new Sprite(Texture.EMPTY);
    sprite.anchor?.set(anchorX, anchorY);
    sprite.visible = false;

    const apply = (baseTexture) => {
        if (animated) {
            const textures = resolveAnimationTextures(resolver, src, descriptor, baseTexture);
            sprite.textures = textures.length ? textures : [Texture.EMPTY];
            sprite.loop = descriptor?.animation?.loop !== false;
            sprite.animationSpeed = Math.max(1, descriptor?.animation?.fps || 8) / 60;
            if (textures.length > 1) sprite.play();
            else sprite.gotoAndStop?.(0);
        } else {
            sprite.texture = resolveTextureFromDescriptor(resolver, src, descriptor, baseTexture);
        }
        clampScale(sprite, width, height, fitMode, scaleMultiplier);
        if (flipX) sprite.scale.x = -Math.abs(sprite.scale.x);
        sprite.visible = true;
    };

    const cached = resolver._textureCache.get(src);
    if (cached) {
        apply(cached);
        return sprite;
    }

    const img = new Image();
    img.onload = () => {
        const texture = Texture.from(img);
        if (texture?.source) {
            texture.source.scaleMode = 'linear';
            texture.source.autoGenerateMipmaps = true;
        }
        resolver._textureCache.set(src, texture);
        apply(texture);
    };
    img.onerror = () => { sprite.visible = false; };
    img.src = src;

    return sprite;
}

/**
 * Eagerly decode the texture behind a legacy "game.slot" key into the
 * resolver's shared cache, so every later sprite build applies its
 * texture SYNCHRONOUSLY (no pop-in). Best-effort: resolves null when
 * the slot has no art or the image fails.
 *
 * @returns {Promise<Texture|null>}
 */
export function preloadSlotTexture(resolver, slotKey) {
    const [game, slot] = splitSlotKey(slotKey);
    if (!game || !slot) return Promise.resolve(null);
    const src = resolver._resolveSrc(game, slot);
    if (!src) return Promise.resolve(null);
    const cached = resolver._textureCache.get(src);
    if (cached) return Promise.resolve(cached);
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const texture = Texture.from(img);
            if (texture?.source) {
                texture.source.scaleMode = 'linear';
                texture.source.autoGenerateMipmaps = true;
            }
            resolver._textureCache.set(src, texture);
            resolve(texture);
        };
        img.onerror = () => resolve(null);
        img.src = src;
    });
}

export function clampScale(sprite, maxWidth, maxHeight, fitMode = 'contain', scaleMultiplier = 1) {
    const baseW = sprite.texture?.width || sprite.width || 1;
    const baseH = sprite.texture?.height || sprite.height || 1;

    if (fitMode === 'native') {
        // Absolute scale: ignore the width/height box entirely and
        // render the PNG at `scaleMultiplier` of its natural pixel
        // size (e.g. 0.24 → 24% of the original art). Used when a
        // caller wants a fixed real-size figure rather than a
        // cell-fitted one.
        const scale = scaleMultiplier > 0 ? scaleMultiplier : 1;
        sprite.scale.set(scale);
        return;
    }

    if (fitMode === 'stretch') {
        // Non-uniform scale: PNG is forced to exactly fill the
        // requested width × height. Useful when the slot footprint
        // is authored to match the desired on-board size and any
        // tiny aspect mismatch with the source PNG is acceptable
        // (e.g. obstacles that must occupy their full cell range).
        const sx = (maxWidth  / baseW) * scaleMultiplier;
        const sy = (maxHeight / baseH) * scaleMultiplier;
        sprite.scale.set(
            Number.isFinite(sx) && sx > 0 ? sx : 1,
            Number.isFinite(sy) && sy > 0 ? sy : 1
        );
        return;
    }

    const baseScale = fitMode === 'cover'
        ? Math.max(maxWidth / baseW, maxHeight / baseH)
        : Math.min(maxWidth / baseW, maxHeight / baseH);
    const scale = baseScale * scaleMultiplier;
    sprite.scale.set(Number.isFinite(scale) && scale > 0 ? scale : 1);
}

export function resolveTextureFromDescriptor(resolver, src, descriptor, baseTexture) {
    const atlas = sanitizeAtlasGrid(descriptor?.atlas);
    if (!atlas || descriptor?.mappingType !== 'gridRegion') return baseTexture;
    return resolveGridTexture(resolver, src, atlas, baseTexture);
}

export function resolveAnimationTextures(resolver, src, descriptor, baseTexture) {
    const atlas = sanitizeAtlasGrid(descriptor?.atlas);
    const animation = descriptor?.animation || null;
    if (!atlas || descriptor?.mappingType !== 'gridRegion' || !animation || animation.mode !== 'frames') {
        return [resolveTextureFromDescriptor(resolver, src, descriptor, baseTexture)];
    }

    const totalFrames = Math.max(1, Number(animation.frames) || 1);
    const startCol = Math.max(0, Number(animation.startCol) || 0);
    const startRow = Math.max(0, Number(animation.startRow) || 0);
    const textures = [];

    for (let i = 0; i < totalFrames; i++) {
        const flatIndex = startRow * atlas.gridCols + startCol + i;
        const frameRow = Math.floor(flatIndex / atlas.gridCols);
        const frameCol = flatIndex % atlas.gridCols;
        if (frameRow >= atlas.gridRows) break;
        textures.push(resolveGridTexture(resolver, src, { ...atlas, frameCol, frameRow }, baseTexture));
    }

    return textures.length ? textures : [resolveTextureFromDescriptor(resolver, src, descriptor, baseTexture)];
}

export function resolveGridTexture(resolver, src, atlas, baseTexture) {
    const safeAtlas = sanitizeAtlasGrid(atlas);
    if (!safeAtlas) return baseTexture;

    // Half-texel inset on every side: with linear filtering, sampling a
    // sub-rect right up to its edge blends in pixels from the NEIGHBOUR
    // atlas cell (classic tile bleeding — shows up as thin coloured
    // lines along tile borders, e.g. grass from the template row above).
    // Losing half a source texel is invisible at cell-render sizes.
    const INSET = 0.5;
    const cellW = Math.max(1, baseTexture.width / safeAtlas.gridCols);
    const cellH = Math.max(1, baseTexture.height / safeAtlas.gridRows);
    const frameX = Math.max(0, Math.min(baseTexture.width - 1, safeAtlas.frameCol * cellW + INSET));
    const frameY = Math.max(0, Math.min(baseTexture.height - 1, safeAtlas.frameRow * cellH + INSET));
    const frameW = Math.max(1, Math.min(baseTexture.width - frameX, cellW * safeAtlas.spanCols - INSET * 2));
    const frameH = Math.max(1, Math.min(baseTexture.height - frameY, cellH * safeAtlas.spanRows - INSET * 2));

    const subKey = `${src}::${safeAtlas.gridCols}:${safeAtlas.gridRows}:${safeAtlas.frameCol}:${safeAtlas.frameRow}:${safeAtlas.spanCols}:${safeAtlas.spanRows}`;
    const cached = resolver._subTextureCache.get(subKey);
    if (cached) return cached;

    const cropped = new Texture({
        source: baseTexture.source,
        frame: new Rectangle(frameX, frameY, frameW, frameH),
    });
    resolver._subTextureCache.set(subKey, cropped);
    return cropped;
}

/**
 * Build a colored Graphics rect using the slot's fallbackColor + cell footprint.
 * Useful as a last-resort visual when no PNG is configured and the caller
 * doesn't have its own placeholder. (Anchored at center.)
 */
export function placeholderGraphic(resolver, game, slot, width, height) {
    const element = resolver._config?.games?.[game]?.elements?.[slot];
    const color = element?.fallbackColor ?? 0x4b5563;
    const g = new Graphics();
    g.roundRect(-width / 2, -height / 2, width, height, Math.min(width, height) * 0.1);
    g.fill({ color, alpha: 1 });
    g.stroke({ color: 0x000000, alpha: 0.25, width: 2 });
    return g;
}
